import {
  useState,
  useEffect,
  useMemo,
  useRef,
  useCallback,
  type MouseEvent as ReactMouseEvent,
} from "react";
import { Outlet, useNavigate, useParams } from "react-router-dom";
import {
  PanelLeft,
  Search,
  Plus,
  MessageCircle,
  Users,
  Pin,
  BellOff,
  Bell,
  X,
  Settings2,
  Archive,
  Trash2,
  Volume2,
  PinOff,
} from "lucide-react";
import { cn } from "@slumbr/ui";

import { useIsMobile } from "@/hooks/use-is-mobile";
import { useAppBadgesStore } from "@/stores/app-badges-store";
import { usePresenceStore } from "@/stores/presence-store";
import type { PresenceEffectiveStatus } from "@slumbr/core";
import {
  getCurrentUserId,
  getCurrentUserName,
  useMessengerStore,
} from "../stores/messenger-store";
import { CreateGroupDialog } from "./create-group-dialog";
import { ChatSettingsPanel } from "./chat-settings-panel";
import { useChatSettingsStore } from "../stores/chat-settings-store";
import { senderColor, initialsOf } from "./sender-identity";
import { TwemojiText } from "./twemoji-text";
import type { Conversation } from "../types";

export interface MessengerLayoutContext {
  conversationListOpen: boolean;
  toggleConversationList: () => void;
  openConversationList: () => void;
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function presenceColor(status?: PresenceEffectiveStatus) {
  if (status === "online") return "bg-emerald-500";
  if (status === "away") return "bg-amber-400";
  if (status === "busy") return "bg-[#ff477e]";
  return "bg-[#536471]";
}

import { toDate, toUnixMs } from "../utils/timestamps";

function formatTimestamp(value: number | string): string {
  const date = toDate(value);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60_000);
  const diffHr = Math.floor(diffMs / 3_600_000);
  const diffDay = Math.floor(diffMs / 86_400_000);

  if (diffMin < 1) return "now";
  if (diffMin < 60) return `${diffMin}m`;
  if (diffHr < 24) return `${diffHr}h`;
  if (diffDay < 7) return `${diffDay}d`;
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
  });
}

function getLastMessagePreview(conversation: Conversation): string {
  if (!conversation.lastMessage) return "No messages yet";
  const msg = conversation.lastMessage;
  const isOwn = msg.senderId === getCurrentUserId();
  const prefix = isOwn ? `${getCurrentUserName()}: ` : "";
  if (msg.content.type === "text") {
    const text = msg.content.text;
    return prefix + (text.length > 50 ? text.slice(0, 50) + "..." : text);
  }
  if (msg.content.type === "image") return prefix + "📷 Photo";
  if (msg.content.type === "gif") return prefix + "GIF";
  if (msg.content.type === "file") return prefix + "📎 " + msg.content.fileName;
  if (msg.content.type === "voice") return prefix + "🎙️ Voice note";
  if (msg.content.type === "game_invite") {
    return prefix + `${msg.content.icon} ${msg.content.gameName} invite`;
  }
  if (msg.content.type === "game_inline") {
    return prefix + `${msg.content.icon} ${msg.content.title}`;
  }
  if (msg.content.type === "system") return msg.content.text;
  return "";
}

export function getOtherParticipant(conversation: Conversation) {
  const currentUserId = getCurrentUserId();
  return (
    conversation.participants.find((p) => p.userId !== currentUserId) ??
    conversation.participants[0]
  );
}

export function getDisplayName(conversation: Conversation): string {
  if (conversation.type === "group" && conversation.name) {
    const groupName = conversation.name.trim();
    if (groupName) return groupName;
  }
  const other = getOtherParticipant(conversation);
  const displayName = other?.displayName?.trim();
  if (displayName) return displayName;
  return other?.userId ? `User ${other.userId.slice(0, 6)}` : "Unknown";
}

export function getConversationAvatarUrl(conversation: Conversation) {
  const customAvatar = conversation.avatarUrl?.trim();
  if (customAvatar) return customAvatar;
  if (conversation.type !== "direct") return undefined;
  return getOtherParticipant(conversation)?.avatarUrl?.trim() || undefined;
}

function getParticipantPresence(
  conversation: Conversation,
  statuses: Record<string, { effectiveStatus: PresenceEffectiveStatus }>
): PresenceEffectiveStatus {
  const currentUserId = getCurrentUserId();
  const visibleStatuses = conversation.participants
    .filter((p) => p.userId !== currentUserId)
    .map((p) => statuses[p.userId]?.effectiveStatus ?? "offline");
  if (conversation.type === "group") {
    if (visibleStatuses.includes("online")) return "online";
    if (visibleStatuses.includes("busy")) return "busy";
    if (visibleStatuses.includes("away")) return "away";
    return "offline";
  }
  return visibleStatuses[0] ?? "offline";
}

// ─── Avatar ──────────────────────────────────────────────────────────────────


function ConversationAvatar({
  conversation,
  size = "md",
  showOnline = false,
}: {
  conversation: Conversation;
  size?: "sm" | "md" | "lg";
  showOnline?: boolean;
}) {
  const [failedUrl, setFailedUrl] = useState<string | null>(null);
  const displayName = getDisplayName(conversation);
  const avatarUrl = getConversationAvatarUrl(conversation);
  const statuses = usePresenceStore((state) => state.statuses);
  const fallbackId =
    conversation.type === "direct"
      ? getOtherParticipant(conversation)?.userId ?? conversation.id
      : conversation.id;
  const sizeClasses =
    size === "sm"
      ? "h-8 w-8 text-xs"
      : size === "lg"
        ? "h-12 w-12 text-base"
        : "h-11 w-11 text-sm";
  const onlineDotSize =
    size === "sm" ? "h-2.5 w-2.5" : size === "lg" ? "h-3.5 w-3.5" : "h-3 w-3";
  const presence = showOnline ? getParticipantPresence(conversation, statuses) : "offline";

  const tone = senderColor(fallbackId);
  const avatarContent = avatarUrl && failedUrl !== avatarUrl ? (
    <img
      src={avatarUrl}
      alt={displayName}
      loading="lazy"
      decoding="async"
      onError={() => setFailedUrl(avatarUrl)}
      className={cn("shrink-0 rounded-full object-cover", sizeClasses)}
    />
  ) : (
    <div
      className={cn(
        "flex shrink-0 items-center justify-center rounded-full bg-white/[0.07] font-bold",
        sizeClasses
      )}
      style={{ color: tone, boxShadow: `inset 0 0 0 1.5px ${tone}59` }}
    >
      {conversation.type === "group" ? (
        <Users
          className={
            size === "sm" ? "h-3.5 w-3.5" : size === "lg" ? "h-5 w-5" : "h-4 w-4"
          }
        />
      ) : (
        initialsOf(displayName)
      )}
    </div>
  );

  return (
    <div className="relative shrink-0">
      {avatarContent}
      {showOnline && (
        <div
          className={cn(
            "absolute bottom-0 right-0 rounded-full border-2 border-[#111417]",
            presenceColor(presence),
            onlineDotSize
          )}
        />
      )}
    </div>
  );
}

// ─── Swipeable Conversation Item ─────────────────────────────────────────────

const SWIPE_THRESHOLD = 70;

function ConversationItem({
  conversation,
  isActive,
  onSelect,
  onArchive,
  onDelete,
  onMuteToggle,
  onPinToggle,
  typingText,
}: {
  conversation: Conversation;
  isActive: boolean;
  onSelect: () => void;
  onArchive: (id: string) => void;
  onDelete: (id: string) => void;
  onMuteToggle: (id: string) => void;
  onPinToggle: (id: string) => void;
  typingText?: string;
}) {
  const displayName = getDisplayName(conversation);
  const preview = typingText || getLastMessagePreview(conversation);
  const timestamp = conversation.lastMessage
    ? formatTimestamp(conversation.lastMessage.createdAt)
    : "";

  // Swipe state
  const [swipeX, setSwipeX] = useState(0);
  const [swiping, setSwiping] = useState(false);
  const [contextMenu, setContextMenu] = useState<{
    x: number;
    y: number;
  } | null>(null);
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const myParticipant = conversation.participants.find(
    (p) => p.userId === getCurrentUserId()
  );
  const canDeleteConversation =
    conversation.type === "direct" || myParticipant?.role === "owner";

  useEffect(() => {
    if (!contextMenu) return;

    const close = () => setContextMenu(null);
    const handleMouseDown = (event: MouseEvent) => {
      if (menuRef.current?.contains(event.target as Node)) return;
      close();
    };
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") close();
    };

    document.addEventListener("mousedown", handleMouseDown);
    document.addEventListener("keydown", handleKeyDown);
    window.addEventListener("scroll", close, true);
    return () => {
      document.removeEventListener("mousedown", handleMouseDown);
      document.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("scroll", close, true);
    };
  }, [contextMenu]);

  const handleContextMenu = useCallback((event: ReactMouseEvent) => {
    event.preventDefault();
    setSwipeX(0);
    setContextMenu({
      x: Math.min(event.clientX, window.innerWidth - 224),
      y: Math.min(event.clientY, window.innerHeight - 236),
    });
  }, []);

  const handleTouchStart = useCallback((e: React.TouchEvent) => {
    touchStartRef.current = {
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
    };
  }, []);

  const handleTouchMove = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;
    const dx = e.touches[0].clientX - touchStartRef.current.x;
    const dy = Math.abs(e.touches[0].clientY - touchStartRef.current.y);
    // Only trigger horizontal swipe
    if (dy > 30 && !swiping) {
      touchStartRef.current = null;
      return;
    }
    if (Math.abs(dx) > 10) {
      setSwiping(true);
      // Clamp swipe: left only (negative = reveal right actions)
      setSwipeX(Math.max(-140, Math.min(0, dx)));
    }
  }, [swiping]);

  const handleTouchEnd = useCallback(() => {
    touchStartRef.current = null;
    if (Math.abs(swipeX) > SWIPE_THRESHOLD) {
      // Snap open
      setSwipeX(-140);
    } else {
      setSwipeX(0);
    }
    setSwiping(false);
  }, [swipeX]);

  const closeSwipe = useCallback(() => setSwipeX(0), []);

  return (
    <div className="relative mx-2 overflow-hidden rounded-2xl" onContextMenu={handleContextMenu}>
      {/* Swipe reveal actions (behind the item) */}
      {swipeX < 0 && (
        <div className="absolute inset-y-0 right-0 flex items-stretch">
          <button
            onClick={() => {
              onMuteToggle(conversation.id);
              closeSwipe();
            }}
            className="flex w-[46px] items-center justify-center bg-yellow-500 text-white"
          >
            <Volume2 className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              onArchive(conversation.id);
              closeSwipe();
            }}
            className="flex w-[46px] items-center justify-center bg-blue-500 text-white"
          >
            <Archive className="h-4 w-4" />
          </button>
          <button
            onClick={() => {
              if (!canDeleteConversation) return;
              onDelete(conversation.id);
              closeSwipe();
            }}
            disabled={!canDeleteConversation}
            className={cn(
              "flex w-[46px] items-center justify-center text-white",
              canDeleteConversation ? "bg-red-500" : "bg-gray-400"
            )}
          >
            <Trash2 className="h-4 w-4" />
          </button>
        </div>
      )}

      {/* Main item content */}
      <button
        onClick={swipeX < -10 ? closeSwipe : onSelect}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        style={{
          transform: `translateX(${swipeX}px)`,
          transition: swiping ? "none" : "transform 200ms ease-out",
        }}
        className={cn(
          "relative flex w-full items-center gap-3 rounded-2xl px-3 py-2.5 text-left transition",
          isActive ? "bg-[#ff477e]/[0.10]" : "hover:bg-white/[0.04]"
        )}
      >
        {isActive && (
          <span className="absolute inset-y-2.5 left-0 w-[3px] rounded-r-full bg-[#ff6b8f]" aria-hidden />
        )}
        <ConversationAvatar conversation={conversation} showOnline />

        <div className="min-w-0 flex-1">
          <div className="flex items-center justify-between gap-2">
            <span
              className={cn(
                "truncate text-sm tracking-tight",
                conversation.unreadCount > 0
                  ? "font-bold text-[#e7e9ea]"
                  : "font-semibold text-[#b9c0c7]"
              )}
            >
              {displayName}
            </span>
            <div className="flex shrink-0 items-center gap-1">
              {conversation.isPinned && (
                <Pin className="h-3 w-3 rotate-45 text-[#5f6b78]" />
              )}
              {conversation.isMuted && (
                <BellOff className="h-3 w-3 text-[#5f6b78]" />
              )}
              <span
                className={cn(
                  "text-[11px] tabular-nums",
                  conversation.unreadCount > 0
                    ? "font-bold text-[#ff8fac]"
                    : "text-[#5f6b78]"
                )}
              >
                {timestamp}
              </span>
            </div>
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <p
              className={cn(
                "truncate text-xs leading-relaxed",
                typingText
                  ? "font-medium italic text-[#ff8fac]"
                  : conversation.unreadCount > 0
                    ? "text-[#c3c9d0]"
                    : "text-[#71767b]"
              )}
            >
              <TwemojiText text={preview} />
            </p>
            {conversation.unreadCount > 0 && (
              <span
                className={cn(
                  "flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full px-1.5 text-[10px] font-black text-white",
                  conversation.isMuted ? "bg-white/20" : "bg-[#ff477e]"
                )}
              >
                {conversation.unreadCount > 99
                  ? "99+"
                  : conversation.unreadCount}
              </span>
            )}
          </div>
        </div>
      </button>
      {contextMenu && (
        <div
          ref={menuRef}
          className="fixed z-[100] w-52 overflow-hidden rounded-2xl border border-white/[0.08] bg-[#14171c]/97 py-1 shadow-[0_20px_50px_-12px_rgba(0,0,0,0.85)] backdrop-blur-xl"
          style={{ left: contextMenu.x, top: contextMenu.y }}
        >
          <button
            onClick={() => {
              setContextMenu(null);
              onSelect();
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[#d7dbdc] transition-colors hover:bg-white/[0.06]"
          >
            <MessageCircle className="h-4 w-4 text-[#71767b]" />
            Open Conversation
          </button>
          <button
            onClick={() => {
              setContextMenu(null);
              onPinToggle(conversation.id);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[#d7dbdc] transition-colors hover:bg-white/[0.06]"
          >
            {conversation.isPinned ? (
              <PinOff className="h-4 w-4 text-[#71767b]" />
            ) : (
              <Pin className="h-4 w-4 text-[#71767b]" />
            )}
            {conversation.isPinned ? "Unpin Conversation" : "Pin Conversation"}
          </button>
          <button
            onClick={() => {
              setContextMenu(null);
              onMuteToggle(conversation.id);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[#d7dbdc] transition-colors hover:bg-white/[0.06]"
          >
            {conversation.isMuted ? (
              <Bell className="h-4 w-4 text-[#71767b]" />
            ) : (
              <BellOff className="h-4 w-4 text-[#71767b]" />
            )}
            {conversation.isMuted ? "Unmute" : "Mute"}
          </button>
          <button
            onClick={() => {
              setContextMenu(null);
              onArchive(conversation.id);
            }}
            className="flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm text-[#d7dbdc] transition-colors hover:bg-white/[0.06]"
          >
            <Archive className="h-4 w-4 text-[#71767b]" />
            Archive
          </button>
          <div className="mx-2 my-1 border-t border-white/[0.06]" />
          <button
            onClick={() => {
              if (!canDeleteConversation) return;
              setContextMenu(null);
              onDelete(conversation.id);
            }}
            disabled={!canDeleteConversation}
            className={cn(
              "flex w-full items-center gap-2.5 px-3 py-2 text-left text-sm transition-colors",
              canDeleteConversation
                ? "text-red-400 hover:bg-red-500/10"
                : "cursor-not-allowed text-[#5f6b78] opacity-60"
            )}
          >
            <Trash2 className="h-4 w-4" />
            {canDeleteConversation
              ? conversation.type === "group"
                ? "Delete Group"
                : "Delete Conversation"
              : "Only Owner Can Delete"}
          </button>
        </div>
      )}
    </div>
  );
}

// ─── Sidebar Content ─────────────────────────────────────────────────────────

type ConversationFilter = "all" | "unread" | "groups";

const FILTERS: Array<{ id: ConversationFilter; label: string }> = [
  { id: "all", label: "All" },
  { id: "unread", label: "Unread" },
  { id: "groups", label: "Groups" },
];

function SidebarContent({
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
}: {
  onSelectConversation: (id: string, messageId?: string) => void;
  onNewConversation: () => void;
  onDeleteConversation: (id: string) => void;
}) {
  const [filter, setFilter] = useState<ConversationFilter>("all");
  const [showSettings, setShowSettings] = useState(false);
  const searchQuery = useMessengerStore((s) => s.searchQuery);
  const setSearchQuery = useMessengerStore((s) => s.setSearchQuery);
  const isLoading = useMessengerStore((s) => s.isLoading);
  const listError = useMessengerStore((s) => s.error);
  const retryFetch = useMessengerStore((s) => s.fetchConversations);
  const allConversations = useMessengerStore((s) => s.conversations);
  const allMessages = useMessengerStore((s) => s.messages);
  const activeConversationId = useMessengerStore(
    (s) => s.activeConversationId
  );
  const typingIndicators = useMessengerStore((s) => s.typingIndicators);
  const isAppearOffline = usePresenceStore((s) => s.isAppearOffline);
  const fetchPresence = usePresenceStore((s) => s.fetchPresence);
  const setPresenceStatus = usePresenceStore((s) => s.setStatus);
  const archiveConversation = useMessengerStore((s) => s.archiveConversation);
  const muteConversation = useMessengerStore((s) => s.muteConversation);
  const unmuteConversation = useMessengerStore((s) => s.unmuteConversation);
  const pinConversation = useMessengerStore((s) => s.pinConversation);
  const unpinConversation = useMessengerStore((s) => s.unpinConversation);

  // Derive filtered list in the component
  const conversations = useMemo(() => {
    let filtered = allConversations.filter((c) => !c.isArchived);

    if (filter === "unread") filtered = filtered.filter((c) => c.unreadCount > 0);
    if (filter === "groups") filtered = filtered.filter((c) => c.type === "group");

    if (searchQuery) {
      const q = searchQuery.toLowerCase();
      filtered = filtered.filter((c) => {
        const name = getDisplayName(c).toLowerCase();
        if (name.includes(q)) return true;
        if (
          c.lastMessage?.content.type === "text" &&
          c.lastMessage.content.text.toLowerCase().includes(q)
        )
          return true;
        return false;
      });
    }

    // Sort: pinned first, then by updatedAt descending
    filtered.sort((a, b) => {
      if (a.isPinned !== b.isPinned) return a.isPinned ? -1 : 1;
      return (
        toUnixMs(b.updatedAt) - toUnixMs(a.updatedAt)
      );
    });

    return filtered;
  }, [allConversations, searchQuery, filter]);

  // Cross-conversation message search. Walks the cached messages map
  // (already in localStorage, see messenger-store) and returns hits with
  // their conversation. Fast enough at chat scale; if libraries grow into
  // five-figure message counts we'd switch to a backend-indexed endpoint.
  const messageHits = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    if (q.length < 2) return [];
    const hits: Array<{
      conversationId: string;
      messageId: string;
      conversationName: string;
      snippet: string;
      timestamp: number | string;
    }> = [];
    const convById = new Map(allConversations.map((c) => [c.id, c]));
    for (const [convId, list] of Object.entries(allMessages)) {
      const conv = convById.get(convId);
      if (!conv || conv.isArchived) continue;
      for (const m of list) {
        if (m.isDeleted) continue;
        if (m.content.type !== "text") continue;
        const lower = m.content.text.toLowerCase();
        if (!lower.includes(q)) continue;
        hits.push({
          conversationId: convId,
          messageId: m.id,
          conversationName: getDisplayName(conv),
          snippet: m.content.text,
          timestamp: m.createdAt,
        });
      }
    }
    hits.sort((a, b) => toUnixMs(b.timestamp) - toUnixMs(a.timestamp));
    return hits.slice(0, 50);
  }, [searchQuery, allMessages, allConversations]);

  // Only subscribe to presence for visible conversation rows. A user with
  // 300 historical contacts shouldn't maintain 300 live presence
  // subscriptions — Momento bills per-subscriber per-event. Cap at the
  // top ~20 by recency + the active conversation's participants. Inactive
  // contacts fall back to a snapshot (one-time fetch).
  const VISIBLE_PRESENCE_CONVERSATIONS = 20;
  const presenceUserIdsKey = useMemo(() => {
    const currentUserId = getCurrentUserId();
    const visibleConvIds = new Set<string>(
      conversations.slice(0, VISIBLE_PRESENCE_CONVERSATIONS).map((c) => c.id)
    );
    if (activeConversationId) visibleConvIds.add(activeConversationId);
    const visibleParticipants = new Set<string>();
    for (const conversation of allConversations) {
      if (!visibleConvIds.has(conversation.id)) continue;
      for (const participant of conversation.participants) {
        if (participant.userId !== currentUserId) visibleParticipants.add(participant.userId);
      }
    }
    return Array.from(visibleParticipants).sort().join("|");
  }, [conversations, allConversations, activeConversationId]);

  // Long-tail snapshot: every conversation participant we *don't* live-
  // subscribe to gets a single REST snapshot read so the avatar dot
  // renders correctly. No realtime; the dot can be stale, which is fine
  // for off-screen contacts.
  const snapshotUserIdsKey = useMemo(() => {
    const currentUserId = getCurrentUserId();
    const liveSet = new Set(presenceUserIdsKey ? presenceUserIdsKey.split("|") : []);
    const long: string[] = [];
    for (const conversation of allConversations) {
      for (const participant of conversation.participants) {
        if (participant.userId === currentUserId) continue;
        if (liveSet.has(participant.userId)) continue;
        long.push(participant.userId);
      }
    }
    return Array.from(new Set(long)).sort().join("|");
  }, [allConversations, presenceUserIdsKey]);

  useEffect(() => {
    const ids = presenceUserIdsKey ? presenceUserIdsKey.split("|") : [];
    if (ids.length === 0) return;

    void fetchPresence(ids).catch(() => {});

    // Subscribe only if we're the tab-leader; followers receive presence
    // updates via the realtime BroadcastChannel mirror.
    let disposed = false;
    let subscription: { unsubscribe: () => void } | null = null;
    let leaderUnsub: (() => void) | null = null;
    let broadcastUnsub: (() => void) | null = null;

    const stop = () => {
      subscription?.unsubscribe();
      subscription = null;
    };

    void import("@/realtime/tab-leader").then(({ broadcastFromLeader, ensureLeaderElection, isTabLeader, subscribeToBroadcasts, subscribeToLeaderChange }) => {
      const setPresenceStatusFromRealtime = (status: Parameters<typeof setPresenceStatus>[0]) => {
        setPresenceStatus(status);
        if (isTabLeader()) broadcastFromLeader({ type: "presence", payload: status });
      };
      const start = async () => {
        const { subscribeToPresence } = await import("../momento-subscriber");
        if (disposed) return;
        subscription = subscribeToPresence(ids, setPresenceStatusFromRealtime, (err) => {
          console.error("[Presence] subscription error:", err);
        });
      };
      void ensureLeaderElection().then(() => {
        if (disposed) return;
        leaderUnsub = subscribeToLeaderChange((leader) => {
          if (leader && !subscription) void start();
          else if (!leader) stop();
        });
        broadcastUnsub = subscribeToBroadcasts((envelope) => {
          if (isTabLeader()) return;
          if (envelope.type !== "presence") return;
          const status = envelope.payload as Parameters<typeof setPresenceStatus>[0];
          if (status?.userId) setPresenceStatus(status);
        });
      });
    });

    return () => {
      disposed = true;
      stop();
      leaderUnsub?.();
      broadcastUnsub?.();
    };
  }, [fetchPresence, presenceUserIdsKey, setPresenceStatus]);

  // Snapshot-only refresh for off-screen contacts. Fire once per change
  // — no live subscription so Momento billing stays flat regardless of
  // contact-graph size.
  useEffect(() => {
    const ids = snapshotUserIdsKey ? snapshotUserIdsKey.split("|") : [];
    if (ids.length === 0) return;
    void fetchPresence(ids).catch(() => {});
  }, [fetchPresence, snapshotUserIdsKey]);

  // Build typing text map: conversationId → "Alice is typing..."
  const typingMap = useMemo(() => {
    const map = new Map<string, string>();
    if (isAppearOffline) return map;
    const grouped = new Map<string, string[]>();

    for (const t of typingIndicators) {
      if (t.userId === getCurrentUserId()) continue;
      const names = grouped.get(t.conversationId) ?? [];
      names.push(t.displayName.split(" ")[0]); // first name only
      grouped.set(t.conversationId, names);
    }

    for (const [convId, names] of grouped) {
      if (names.length === 1) {
        map.set(convId, `${names[0]} is typing...`);
      } else if (names.length === 2) {
        map.set(convId, `${names[0]} and ${names[1]} are typing...`);
      } else {
        map.set(convId, `${names.length} people are typing...`);
      }
    }

    return map;
  }, [typingIndicators, isAppearOffline]);

  const handleMuteToggle = useCallback(
    (id: string) => {
      const conv = allConversations.find((c) => c.id === id);
      if (conv?.isMuted) {
        unmuteConversation(id);
      } else {
        muteConversation(id);
      }
    },
    [allConversations, muteConversation, unmuteConversation]
  );

  const handlePinToggle = useCallback(
    (id: string) => {
      const conv = allConversations.find((c) => c.id === id);
      if (conv?.isPinned) {
        unpinConversation(id);
      } else {
        pinConversation(id);
      }
    },
    [allConversations, pinConversation, unpinConversation]
  );

  // Total unread count for header
  const totalUnread = useMemo(
    () => allConversations.reduce((sum, c) => sum + c.unreadCount, 0),
    [allConversations]
  );

  return (
    <div className="flex h-full flex-col bg-[#0b0d0f] text-[#e7e9ea]">
      <header className="shrink-0 px-4 pb-3 pt-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-[22px] font-black tracking-tight text-[#e7e9ea]">Chats</h2>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setShowSettings(true)}
              className="flex h-9 w-9 items-center justify-center rounded-full text-[#71767b] transition hover:bg-white/[0.06] hover:text-[#e7e9ea]"
              aria-label="Chat settings"
              title="Chat settings"
            >
              <Settings2 className="h-[18px] w-[18px]" />
            </button>
            <button
              onClick={onNewConversation}
              className="flex h-9 w-9 items-center justify-center rounded-full bg-[#ff477e] text-white shadow-[0_6px_18px_-6px_#ff477e] transition hover:bg-[#ff5c8d] active:scale-95"
              aria-label="New conversation"
              title="New conversation"
            >
              <Plus className="h-[18px] w-[18px]" />
            </button>
          </div>
        </div>

        <div className="mt-3 flex items-center gap-2 rounded-full bg-white/[0.05] px-3 py-2 ring-1 ring-inset ring-white/[0.06] transition focus-within:bg-white/[0.07] focus-within:ring-white/[0.14]">
          <Search className="h-4 w-4 shrink-0 text-[#71767b]" />
          <input
            type="text"
            placeholder="Search chats and messages"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="min-w-0 flex-1 bg-transparent text-[13.5px] text-[#e7e9ea] outline-none placeholder:text-[#4d5866]"
          />
          {searchQuery && (
            <button
              type="button"
              onClick={() => setSearchQuery("")}
              className="shrink-0 rounded-full p-0.5 text-[#71767b] transition hover:text-[#e7e9ea]"
              aria-label="Clear search"
            >
              <X className="h-3.5 w-3.5" />
            </button>
          )}
        </div>

        <div className="mt-2.5 flex items-center gap-1">
          {FILTERS.map((f) => {
            const active = filter === f.id;
            const count = f.id === "unread" ? totalUnread : undefined;
            return (
              <button
                key={f.id}
                type="button"
                onClick={() => setFilter(f.id)}
                className={cn(
                  "inline-flex items-center gap-1.5 rounded-full px-3 py-1 text-[11.5px] font-bold transition",
                  active
                    ? "bg-[#ff477e]/15 text-[#ff8fac]"
                    : "text-[#71767b] hover:bg-white/[0.05] hover:text-[#d7dbdc]"
                )}
              >
                {f.label}
                {count != null && count > 0 && (
                  <span
                    className={cn(
                      "rounded-full px-1.5 text-[10px] font-black tabular-nums",
                      active ? "bg-[#ff477e]/25" : "bg-white/[0.08] text-[#9aa4b0]"
                    )}
                  >
                    {count > 99 ? "99+" : count}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      </header>

      {/* Cross-conversation message hits — visible whenever the user has
          typed 2+ chars. Click one to jump into the conversation and
          scroll to that message via the existing `?msg=` query param. */}
      {searchQuery.trim().length >= 2 && messageHits.length > 0 && (
        <div className="border-b border-white/[0.04]">
          <p className="px-5 pt-3 pb-2 text-[10px] font-black uppercase tracking-[0.22em] text-[#536471]">
            Messages ({messageHits.length})
          </p>
          <div className="max-h-60 overflow-y-auto">
            {messageHits.map((hit) => (
              <button
                key={`${hit.conversationId}-${hit.messageId}`}
                onClick={() => {
                  onSelectConversation(hit.conversationId, hit.messageId);
                }}
                className="block w-full px-5 py-2 text-left transition hover:bg-white/[0.03]"
              >
                <div className="truncate text-sm font-bold text-[#e7e9ea]">{hit.conversationName}</div>
                <div className="truncate text-[11px] text-[#71767b]">
                  <TwemojiText text={hit.snippet} />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex-1 overflow-y-auto">
        {conversations.length === 0 && isLoading ? (
          // Never show "No chats yet" while the first fetch is in flight —
          // a fresh device briefly has zero cached conversations and the
          // definitive empty copy made a loading state look like data loss.
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="h-6 w-6 animate-spin rounded-full border-2 border-white/10 border-t-[#d7dbdc]" />
            <p className="mt-3 text-xs text-[#71767b]">Loading chats…</p>
          </div>
        ) : conversations.length === 0 && listError ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.03] text-[#536471]">
              <MessageCircle className="h-6 w-6" />
            </div>
            <p className="mt-3 text-sm font-bold text-[#d7dbdc]">
              Couldn&apos;t load your chats
            </p>
            <p className="mt-1 text-xs text-[#71767b]">
              Check your connection and try again.
            </p>
            <button
              onClick={() => void retryFetch()}
              className="mt-4 rounded-full bg-white/[0.06] px-4 py-1.5 text-xs font-bold text-[#e7e9ea] transition hover:bg-white/[0.1]"
            >
              Retry
            </button>
          </div>
        ) : conversations.length === 0 ? (
          <div className="flex flex-col items-center justify-center px-6 py-16 text-center">
            <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/[0.03] text-[#536471]">
              <MessageCircle className="h-6 w-6" />
            </div>
            <p className="mt-3 text-sm font-bold text-[#d7dbdc]">
              {searchQuery
                ? "No chats match"
                : filter === "unread"
                  ? "All caught up"
                  : filter === "groups"
                    ? "No groups yet"
                    : "No chats yet"}
            </p>
            {!searchQuery && filter === "all" && (
              <p className="mt-1 text-xs text-[#71767b]">
                Start a new conversation to get going.
              </p>
            )}
          </div>
        ) : (
          <ul className="flex flex-col gap-0.5 pb-3 pt-0.5">
            {conversations.map((conv) => (
              <ConversationItem
                key={conv.id}
                conversation={conv}
                isActive={conv.id === activeConversationId}
                onSelect={() => onSelectConversation(conv.id)}
                onArchive={archiveConversation}
                onDelete={onDeleteConversation}
                onMuteToggle={handleMuteToggle}
                onPinToggle={handlePinToggle}
                typingText={typingMap.get(conv.id)}
              />
            ))}
          </ul>
        )}
      </div>

      <ChatSettingsPanel isOpen={showSettings} onClose={() => setShowSettings(false)} />
    </div>
  );
}

// ─── Layout ──────────────────────────────────────────────────────────────────

export function MessengerLayout() {
  const navigate = useNavigate();
  const { conversationId } = useParams();
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [showNewConversation, setShowNewConversation] = useState(false);
  const setActiveConversation = useMessengerStore(
    (s) => s.setActiveConversation
  );
  const fetchConversations = useMessengerStore((s) => s.fetchConversations);
  const conversations = useMessengerStore((s) => s.conversations);
  const activeConversationId = useMessengerStore((s) => s.activeConversationId);
  const deleteConversation = useMessengerStore((s) => s.deleteConversation);
  const clearMessengerBadge = useAppBadgesStore((s) => s.clearBadge);

  // A fresh device has no local copy of your chat colour. The backend stamps
  // it onto every participant snapshot, including your own, so read it back
  // from there rather than spending a separate profile request on it.
  const adoptServerColor = useChatSettingsStore((s) => s.adoptServerColor);
  useEffect(() => {
    const me = getCurrentUserId();
    for (const conversation of conversations) {
      const mine = conversation.participants.find((p) => p.userId === me);
      if (mine?.chatColor) {
        adoptServerColor(mine.chatColor);
        return;
      }
    }
  }, [conversations, adoptServerColor]);

  // Fetch conversations on mount — ALWAYS, not just when the list is empty.
  // The store dedupes concurrent fetches and retries transient failures, so
  // this is cheap; skipping it left warm-cache devices stale and gave a
  // fresh device exactly one silent chance to load anything.
  useEffect(() => {
    clearMessengerBadge("messenger");
    fetchConversations().catch(() => {});
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleSelectConversation = (id: string, messageId?: string) => {
    clearMessengerBadge("messenger");
    setActiveConversation(id);
    navigate(`/messenger/${id}${messageId ? `?msg=${encodeURIComponent(messageId)}` : ""}`);
    if (isMobile) setDrawerOpen(false);
  };

  const handleNewConversation = () => {
    setShowNewConversation(true);
  };

  const layoutContext = useMemo<MessengerLayoutContext>(
    () => ({
      conversationListOpen: isMobile ? drawerOpen : sidebarOpen,
      toggleConversationList: () => {
        if (isMobile) {
          setDrawerOpen((open) => {
            const next = !open;
            // Drop the conversation id from the URL when opening the list so
            // the shell knows we're no longer inside a chat (→ bottom nav
            // reappears). Picking a conversation pushes the id back.
            if (next) navigate("/messenger");
            return next;
          });
        } else {
          setSidebarOpen((open) => !open);
        }
      },
      openConversationList: () => {
        if (isMobile) {
          setDrawerOpen(true);
          navigate("/messenger");
        } else {
          setSidebarOpen(true);
        }
      },
    }),
    [drawerOpen, isMobile, sidebarOpen, navigate]
  );

  const handleConversationCreated = (id: string) => {
    handleSelectConversation(id);
  };

  const handleDeleteConversation = useCallback(
    (id: string) => {
      deleteConversation(id);
      if (id === activeConversationId) {
        navigate("/messenger");
        if (isMobile) setDrawerOpen(false);
      }
    },
    [activeConversationId, deleteConversation, isMobile, navigate]
  );

  if (isMobile) {
    if (!conversationId) {
      return (
        <div className="flex h-full flex-col bg-[#0b0d0f]">
          <SidebarContent
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onDeleteConversation={handleDeleteConversation}
          />
          <CreateGroupDialog
            isOpen={showNewConversation}
            onClose={() => setShowNewConversation(false)}
            onCreated={handleConversationCreated}
          />
        </div>
      );
    }

    return (
      <div className="flex h-full flex-col">
        {/* Drawer overlay */}
        <div
          className={cn(
            "fixed inset-0 z-40 bg-black/50 transition-opacity duration-200",
            drawerOpen ? "opacity-100" : "pointer-events-none opacity-0"
          )}
          onClick={() => setDrawerOpen(false)}
        />

        {/* Drawer */}
        <div
          className={cn(
            "fixed left-0 top-0 z-50 h-full w-full bg-[#0b0d0f] shadow-xl transition-transform duration-200",
            drawerOpen ? "translate-x-0" : "-translate-x-full"
          )}
        >
          <div className="absolute right-3 top-3 z-10">
            <button
              onClick={() => setDrawerOpen(false)}
              className="flex h-9 w-9 items-center justify-center rounded-xl bg-white/[0.07] text-[#d7dbdc] shadow-sm backdrop-blur transition hover:bg-white/[0.12]"
              aria-label="Close conversations"
            >
              <PanelLeft className="h-5 w-5" />
            </button>
          </div>
          <SidebarContent
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onDeleteConversation={handleDeleteConversation}
          />
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden">
          <Outlet context={layoutContext} />
        </div>

        {/* Create Group Dialog */}
        <CreateGroupDialog
          isOpen={showNewConversation}
          onClose={() => setShowNewConversation(false)}
          onCreated={handleConversationCreated}
        />
      </div>
    );
  }

  // Desktop layout
  return (
    <div className="relative flex h-full bg-[#0b0d0f] text-[#e7e9ea]">
      {/* Sidebar */}
      {sidebarOpen && (
        <div className="w-80 shrink-0 border-r border-white/[0.06] bg-[#0b0d0f]">
          <SidebarContent
            onSelectConversation={handleSelectConversation}
            onNewConversation={handleNewConversation}
            onDeleteConversation={handleDeleteConversation}
          />
        </div>
      )}

      {/* Content area */}
      <div className="flex-1 overflow-auto">
        <Outlet context={layoutContext} />
      </div>

      {/* Create Group Dialog */}
      <CreateGroupDialog
        isOpen={showNewConversation}
        onClose={() => setShowNewConversation(false)}
        onCreated={handleConversationCreated}
      />
    </div>
  );
}
